import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { CheckIcon, InfoIcon, XIcon } from '../icons';

export type ToastType = 'success' | 'warning' | 'error' | 'info';

interface ToastProps {
  message: string;
  type?: ToastType;
  onDismiss: () => void;
}

const ACCENT: Record<ToastType, string> = {
  success: '#26a494',
  info: '#3e9df3',
  warning: '#f59e0b',
  error: '#e51e1d',
};

function ToastIcon({ type }: { type: ToastType }) {
  const size = 18;
  if (type === 'success') return <CheckIcon size={size} className="toast-icon" />;
  if (type === 'error') return <XIcon size={size} className="toast-icon" />;
  return <InfoIcon size={size} className="toast-icon" />;
}

export default function Toast({ message, type = 'success', onDismiss }: ToastProps) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!visible) {
      const timer = setTimeout(onDismiss, 300);
      return () => clearTimeout(timer);
    }
  }, [visible, onDismiss]);

  const accentColor = ACCENT[type];

  return (
    <Box
      onClick={() => setVisible(false)}
      sx={{
        cursor: 'pointer',
        animation: visible
          ? 'toastSlideIn 0.25s ease-out forwards'
          : 'toastSlideOut 0.3s ease-in forwards',
        '@keyframes toastSlideIn': {
          from: { transform: 'translateX(120%)', opacity: 0 },
          to: { transform: 'translateX(0)', opacity: 1 },
        },
        '@keyframes toastSlideOut': {
          from: { transform: 'translateX(0)', opacity: 1 },
          to: { transform: 'translateX(120%)', opacity: 0 },
        },
      }}
    >
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.75,
          backgroundColor: theme.app.card,
          border: `1px solid ${theme.app.border}`,
          borderLeft: `3px solid ${accentColor}`,
          borderRadius: '12px',
          boxShadow: theme.shadows[4],
          maxWidth: 340,
        })}
      >
        <Box sx={{ color: accentColor, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          <ToastIcon type={type} />
        </Box>
        <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
          {message}
        </Typography>
      </Box>
    </Box>
  );
}
